import Cookies from "js-cookie";
import axios from "axios";
import { post, fetchData } from "@/utils/api-handler";
import apiClient from "@/utils/api-client";

/** Calls this app's own same-origin Next.js route handlers (login/logout/complete-login) directly — never through apiClient, since these specific routes issue/clear the httpOnly session cookies themselves and would otherwise collide with the /api/[...path] catch-all's own auth handling. */
const sameOriginClient = axios.create();


export const DEFAULT_SUBSCRIPTION_PLAN_COOKIE = "default_subscription_plan";

const DEFAULT_PLAN_COOKIE_OPTS = {
  path: "/",
  sameSite: "lax" as const,
  expires: 30,
};

function isDefaultPlanUser(user: User | null): boolean {
  return (
    user?.organization?.billing_subscription?.subscription_plan?.is_default ===
    true
  );
}


export function isPartnerReferredUser(user: User | null): boolean {
  return user?.organization?.is_referred_by_partner === true;
}

/** Partner-referred organizations are never restricted: the partner owns their plan and billing, so the subscription plan is not consulted at all for them. */
export function isWorkforceRestrictedUser(user: User | null): boolean {
  if (isPartnerReferredUser(user)) return false;
  return isDefaultPlanUser(user);
}

export function isTeamsNavLockedClient(user: User | null): boolean {
  if (user) {
    return isWorkforceRestrictedUser(user);
  }
  if (typeof window === "undefined") return false;
  return Cookies.get(DEFAULT_SUBSCRIPTION_PLAN_COOKIE) === "1";
}

export const DEFAULT_PLAN_POST_LOGIN_PATH = "/workforce/command-team/chat";

export function syncDefaultSubscriptionPlanCookieFromUser(
  user: User | null
): void {
  if (typeof window === "undefined") return;
  if (isWorkforceRestrictedUser(user)) {
    Cookies.set(DEFAULT_SUBSCRIPTION_PLAN_COOKIE, "1", DEFAULT_PLAN_COOKIE_OPTS);
  } else {
    Cookies.remove(DEFAULT_SUBSCRIPTION_PLAN_COOKIE, { path: "/" });
  }
}

export function syncDefaultSubscriptionPlanCookieFromStorage(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem("user");
    if (!raw) {
      Cookies.remove(DEFAULT_SUBSCRIPTION_PLAN_COOKIE, { path: "/" });
      return;
    }
    syncDefaultSubscriptionPlanCookieFromUser(JSON.parse(raw) as User);
  } catch {
    Cookies.remove(DEFAULT_SUBSCRIPTION_PLAN_COOKIE, { path: "/" });
  }
}

export function clearDefaultSubscriptionPlanCookie(): void {
  if (typeof window === "undefined") return;
  Cookies.remove(DEFAULT_SUBSCRIPTION_PLAN_COOKIE, { path: "/" });
}

type LoginCredentials = {
  email: string;
  password: string;
};
type LoginResponse = {
  message?: string;
  user?: User | null;
};
type CompleteLoginResponse = {
  access_token: string;
  refresh_token: string;
};

type SignupPayload = {
  user_email: string;
  organization_name: string;
  user_fullname: string;
};

type UpdateForgottenPasswordPayload = {
  new_password: string;
  password_confirm: string;
};

export const authService = {
  signup: async (payload: SignupPayload) => {
    const response = await post("/api/auth/signup", payload);
    return response;
  },

  forgotPassword: async (payload: { email: string }) => {
    return post("/api/auth/forgot-password", payload);
  },

  updateForgottenPassword: async (
    resetToken: string,
    payload: UpdateForgottenPasswordPayload
  ) => {
    return apiClient.patch("/api/auth/update-forgotten-password", payload, {
      headers: { Authorization: `Bearer ${resetToken}` },
    });
  },

  login: async (credentials: LoginCredentials) => {
    return sameOriginClient.post<LoginResponse>("/api/auth/login", credentials);
  },

  superAdminLogin: async (credentials: LoginCredentials) => {
    return sameOriginClient.post<LoginResponse>(
      "/api/auth/super-admin-login",
      credentials
    );
  },

  logout: async () => {
    return sameOriginClient.post("/api/auth/logout");
  },

  me: async (): Promise<User | null> => {
    return fetchData("/api/auth/me");
  },

  initiateEnable2fa: () =>
    apiClient.post<{ message?: string }>("/api/users/initiate-enable-2fa"),

  initiateDisable2fa: () =>
    apiClient.post<{ message?: string }>("/api/users/initiate-disable-2fa"),

  completeEnable2faFromEmail: (emailToken: string) =>
    apiClient.patch<{ message?: string }>("/api/users/enable-2fa", null, {
      headers: { Authorization: `Bearer ${emailToken}` },
    }),

  completeDisable2faFromEmail: (emailToken: string) =>
    apiClient.patch<{ message?: string }>("/api/users/disable-2fa", null, {
      headers: { Authorization: `Bearer ${emailToken}` },
    }),
  completeLogin: async (completeLoginToken: string) => {
    return sameOriginClient.post<{ user: User | null }>("/api/auth/complete-login", {
      token: completeLoginToken,
    });
  },
  completeSignup: async (
    completeSignupToken: string,
    payload: {
      user_email: string;
      user_fullname: string;
      user_password: string;
      user_password_confirm: string;
    }
  ) => {
    return apiClient.post<CompleteLoginResponse>(
      "/api/auth/complete-signup",
      payload,
      {
        headers: { Authorization: `Bearer ${completeSignupToken}` },
      }
    );
  },
};
